import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { usePageTransition } from '../PageTransition';
import { RealWoodenFrame } from './WoodenFrames';

const HeroSection = () => {
  const { t } = useTranslation();
  const { navigateWithTransition } = usePageTransition();
  const [isLoaded, setIsLoaded] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);
  const showClosureBanner = false;

  return (
    <section ref={ref} className="relative min-h-screen bg-[#F5F3EF] pt-24 md:pt-28 pb-8 md:pb-12">
      {showClosureBanner && (
        <motion.div className="absolute top-24 md:top-28 left-0 right-0 z-30 bg-gradient-to-r from-[#8B4513] via-[#C97A2B] to-[#8B4513] text-white py-6 px-4 shadow-xl"
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <div className="pb-3 border-b border-white/20">
              <p className="text-xs tracking-widest uppercase text-white/70 mb-1">Suomeksi</p>
              <p className="text-sm md:text-base font-medium">Ravintolamme on tilapäisesti suljettu muutaman päivän ajan remontin vuoksi.</p>
              <p className="text-sm md:text-base">Toivotamme teidät pian tervetulleiksi takaisin entistä parempaan kokemukseen.</p>
            </div>
            <div className="pb-3 border-b border-white/20">
              <p className="text-xs tracking-widest uppercase text-white/70 mb-1">På Svenska</p>
              <p className="text-sm md:text-base font-medium">Vår restaurang är tillfälligt stängd i några dagar på grund av renovering.</p>
              <p className="text-sm md:text-base">Vi ser fram emot att snart välkomna er tillbaka till en ännu bättre upplevelse.</p>
            </div>
            <div>
              <p className="text-xs tracking-widest uppercase text-white/70 mb-1">In English</p>
              <p className="text-sm md:text-base font-medium">Our restaurant is temporarily closed for a few days due to renovation.</p>
              <p className="text-sm md:text-base">We look forward to welcoming you back soon to an even better experience.</p>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div className={`max-w-[1400px] mx-auto px-4 md:px-6 ${showClosureBanner ? 'pt-72 md:pt-56' : ''}`} style={{ opacity, scale }}>
        <motion.div className="text-center mb-6 md:mb-8" initial={{ opacity: 0, y: 30 }} animate={isLoaded ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1, delay: 0.2 }}>
          <p className="text-[#6B705C] text-xs tracking-[0.3em] uppercase mb-2">{t('hero.welcomeTo')}</p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl text-[#2F2F2F]" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
            <span className="italic">Sunsari</span>
          </h1>
        </motion.div>

        <div className="max-w-sm md:max-w-md lg:max-w-lg mx-auto">
          <RealWoodenFrame src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070" alt="Sunsari Restaurant" isLoaded={isLoaded} />
        </div>

        <motion.div className="text-center mt-6 md:mt-8" initial={{ opacity: 0, y: 20 }} animate={isLoaded ? { opacity: 1, y: 0 } : {}} transition={{ duration: 1, delay: 1.5 }}>
          <p className="text-[#6B705C] text-sm md:text-base font-light mb-6 max-w-md mx-auto">{t('hero.tagline')}</p>
          <motion.div className="flex items-center justify-center gap-4 mb-6" initial={{ opacity: 0 }} animate={isLoaded ? { opacity: 1 } : {}} transition={{ delay: 1.8 }}>
            <div className="w-12 h-px bg-[#B65A3A]" />
            <div className="w-2 h-2 rounded-full bg-[#C97A2B]" />
            <div className="w-12 h-px bg-[#B65A3A]" />
          </motion.div>

          <div className="flex justify-center items-center gap-4 md:gap-8 flex-wrap">
            {[
              { name: t('nav.menu'), path: '/menu', highlight: false },
              { name: t('mobileCta.order'), path: '/order', highlight: true, bg: '#C97A2B' },
              { name: t('mobileCta.reserve'), path: '/combine-tables', highlight: true, bg: '#2F2F2F' },
              { name: t('nav.contact'), path: '/contact', highlight: false }
            ].map((item, index) => (
              <motion.button key={item.name} onClick={() => navigateWithTransition(item.path)}
                className={item.highlight
                  ? "text-white text-xs tracking-[0.15em] uppercase font-bold px-5 py-2.5 transition-all duration-300 hover:opacity-90"
                  : "text-[#2F2F2F] text-xs tracking-[0.15em] uppercase font-light hover:text-[#C97A2B] transition-colors duration-300 pb-1 border-b border-transparent hover:border-[#C97A2B]"
                }
                style={item.highlight ? { backgroundColor: item.bg } : {}}
                initial={{ opacity: 0, y: 10 }} animate={isLoaded ? { opacity: 1, y: 0 } : {}} transition={{ delay: 1.8 + index * 0.1 }}
                whileHover={item.highlight ? { scale: 1.05 } : {}} whileTap={item.highlight ? { scale: 0.97 } : {}}
                data-testid={`hero-nav-${item.path.replace('/', '')}`}>
                {item.name}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
